import { GridColDef } from "@mui/x-data-grid";
import { useFilasEsperaDispatch, useFilasEsperaState } from "./filas-espera-context";
import { ACTION_TYPES, initialDataRequest } from "./filas-espera-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IFilaEsperaResponse } from "../../../../model/admin-portal/response/fila-espera-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';

export const useFilasEsperaController = () => {
    const state = useFilasEsperaState();
    const dispatch = useFilasEsperaDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listFilasEspera(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar filas de espera' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const tipos = await AdminPortalAppService.listTiposFilaEspera();
            dispatch({ type: ACTION_TYPES.setTiposList, payload: tipos });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar lista de Tipos de Fila' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getFilaEspera(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idTipoFilaEspera: response.idTipoFilaEspera,
                    nomeUnidade: response.nomeUnidade || '',
                    quantidadeAguardando: response.quantidadeAguardando,
                    quantidadeVagasTotais: response.quantidadeVagasTotais ?? null,
                    quantidadeMatriculados: response.quantidadeMatriculados ?? null,
                    textoCriteriosPriorizacao: response.textoCriteriosPriorizacao || '',
                    dtaAtualizacao: response.dtaAtualizacao
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar registro de fila de espera' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idTipoFilaEspera) return false;
        if (r.quantidadeAguardando === null || r.quantidadeAguardando === undefined || r.quantidadeAguardando < 0) return false;
        if (!r.dtaAtualizacao) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IFilaEsperaResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateFilaEspera(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Registro atualizado com sucesso' : 'Registro criado com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar registro de fila de espera' });
        } finally {
            showBackDrop(false);
        }
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'tipoFilaEsperaName', headerName: 'Tipo de Fila', width: 160 },
            { field: 'nomeUnidade', headerName: 'Unidade', flex: 1, minWidth: 220 },
            { field: 'quantidadeAguardando', headerName: 'Qtd. Aguardando', width: 150 },
            { field: 'quantidadeVagasTotais', headerName: 'Vagas Totais', width: 120, valueGetter: (_value, row) => row.quantidadeVagasTotais ?? '-' },
            { field: 'quantidadeMatriculados', headerName: 'Matriculados', width: 120, valueGetter: (_value, row) => row.quantidadeMatriculados ?? '-' },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid
    };
};
